import { useFormikContext } from 'formik';
import { ScenarioVersionModalFormValues } from '../versions/version-modal/ScenarioVersionModal';
import { SalvDropdown, SalvFieldHeader, Size } from '@salvapp/ui-components';
import React, { useMemo } from 'react';
import { InternalApiEntityGroup, InternalApiEntityType } from '../../api-internal';
import { toOptions } from '../versions/version-modal/ScenarioVersionLeftPanel';
import { useQuerySegments } from '../../screening/configure/segments/segmentActions';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';

const toDropdownOptions = (segments: string[], options: DropdownOption[]) =>
  segments
    .map((segment) => options.find((option) => option.key === segment))
    .filter((option) => option)
    .map((option) => option!);

const uniqueByCodeArchivedLast = (segments?: InternalApiEntityGroup[]) =>
  segments
    ?.sort((a, b) => {
      if (!a.archivedAt && !b.archivedAt) return 0;
      if (!a.archivedAt) return -1;
      if (!b.archivedAt) return 1;
      return b.archivedAt!.getTime() - a.archivedAt!.getTime();
    })
    .reduce((acc: InternalApiEntityGroup[], segment) => {
      if (!acc.some((s: InternalApiEntityGroup) => s.code === segment.code)) {
        acc.push(segment);
      }
      return acc;
    }, []) || [];

export type SegmentLogicType = 'INCLUDED' | 'EXCLUDED';

interface Props {
  type: SegmentLogicType;
  isTransactionScenario: boolean;
  isDisabled: boolean;
  withDescription: boolean;
  className?: string;
}

const SegmentsEditor = ({ type, isTransactionScenario, isDisabled, withDescription, className = '' }: Props) => {
  const { values, setFieldValue } = useFormikContext<ScenarioVersionModalFormValues>();

  const { data: segments } = useQuerySegments({ includeArchived: true });

  const uniqueSegments = uniqueByCodeArchivedLast(segments);
  const personSegmentOptions = useMemo(() => toOptions(uniqueSegments, InternalApiEntityType.PERSON), [segments]);
  const transactionSegmentOptions = useMemo(
    () => toOptions(uniqueSegments, InternalApiEntityType.TRANSACTION),
    [segments]
  );

  const title = type === 'INCLUDED' ? 'Included Segments' : 'Excluded Segments';
  const description =
    type === 'INCLUDED'
      ? 'Uses AND logic: an Entity must match all of the selected Segments to trigger the Scenario.'
      : 'Uses OR logic: if an Entity matches any of the selected Segments the Scenario is skipped.';
  const fieldName: keyof Pick<ScenarioVersionModalFormValues, 'segments' | 'excludedSegments'> =
    type === 'INCLUDED' ? 'segments' : 'excludedSegments';

  return (
    <div className={className}>
      <SalvFieldHeader label={title} size={Size.SMALL} description={withDescription ? description : undefined} />
      <div className='parameters-view'>
        <div>Person Segments</div>
        <SalvDropdown
          placeholder='Select...'
          disabled={isDisabled}
          isMulti
          options={personSegmentOptions}
          size={Size.SMALL}
          value={toDropdownOptions(values[fieldName], personSegmentOptions)}
          onChange={async (options) => {
            await setFieldValue(
              fieldName,
              toDropdownOptions(values[fieldName], transactionSegmentOptions)
                .concat(options)
                .map((option) => option.key)
            );
          }}
          ariaLabel={title + ' Person Segments'}
        />
        {isTransactionScenario && (
          <>
            <div>Transaction Segments</div>
            <SalvDropdown
              placeholder='Select...'
              disabled={isDisabled}
              isMulti
              options={transactionSegmentOptions}
              size={Size.SMALL}
              value={toDropdownOptions(values[fieldName], transactionSegmentOptions)}
              onChange={async (options) => {
                await setFieldValue(
                  fieldName,
                  toDropdownOptions(values[fieldName], personSegmentOptions)
                    .concat(options)
                    .map((option) => option.key)
                );
              }}
              ariaLabel={title + ' Transaction Segments'}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SegmentsEditor;
